// ChargeCatalogue — step 1 of the record-invoice wizard: a grouped,
// multi-select checkbox catalogue of everything invoiceable on the contract
// (the tag-driven Payables groups), each row showing its price signal so the
// operator can match invoice lines by eye. Selection state lives in the
// modal; this component renders rows and reports toggles. Empty groups are
// hidden; a fully empty catalogue points the operator at step-2 manual
// charges instead — a distinct empty state from the no-search-match one
// below. Above SOW_SEARCH_THRESHOLD rows, a text search (shared matching
// logic with the PO form's tag pickers) narrows the catalogue; filtering is
// render-only, so a hidden ticked row stays selected until the query clears.
// A persistent role="status" region reports the outcome — it doubles as the
// visible no-match message and the sr-only match-count announcer, since
// focus never leaves the search input for either.
//
// When the modal supplies per-payable invoiced totals, fixed-amount
// milestones already fully drawn by approved invoices grey out (an already
// -selected row stays tickable so it can be unticked); partial and
// awaiting-review draws render as hints only — a review invoice may yet be
// queried, and a partially-drawn milestone needs a balance invoice. Rate
// items, SOWs, Other, and variable milestones never grey: they have no
// payable-level cap to judge fullness against.

import { useState } from "react";
import { clsx } from "clsx";
import { formatAud } from "../../../utils/financeCalc";
import type { Payables } from "../../../types/finance";
import type { PayableInvoicedTotals } from "./invoicedByPayable";
import {
  SOW_SEARCH_THRESHOLD,
  matchesSowSearch,
} from "../tracking/shared/sowPickerSearch";

// Matches the money epsilon used across the finance derivations.
const EPSILON = 0.005;

interface CatalogueRow {
  payableId: string;
  label: string;
  price: string;
  /** Draw-state hint shown under the label ("Fully invoiced", partial, or
   *  awaiting-review copy); absent for undrawn or capless rows. */
  hint?: string;
  /** Only ever true for a fixed-amount milestone whose approved draws reach
   *  its expected amount. */
  fullyInvoiced?: boolean;
}

interface CatalogueGroup {
  heading: string;
  rows: CatalogueRow[];
}

interface ChargeCatalogueProps {
  payables: Payables;
  selected: ReadonlySet<string>;
  onToggle: (payableId: string) => void;
  disabled?: boolean;
  /** Per-payable approved/in-review draw totals (edit mode excludes the
   *  invoice being edited). Omitting it disables the greying entirely. */
  invoicedTotals?: ReadonlyMap<string, PayableInvoicedTotals>;
}

function milestoneDrawState(
  expected: number | null,
  totals: PayableInvoicedTotals | undefined,
): Pick<CatalogueRow, "hint" | "fullyInvoiced"> {
  if (!totals || expected === null) return {};
  // The two draw kinds compose rather than short-circuit: a live review
  // invoice on a partially- or fully-drawn milestone is exactly the
  // contention the hint exists to surface (no validation rule reports the
  // two-invoice over-claim — approve one and the other still claims a
  // fully-drawn milestone).
  const inReviewSuffix =
    totals.inReview > EPSILON
      ? ` · ${formatAud(totals.inReview)} awaiting review`
      : "";
  if (totals.approved >= expected - EPSILON) {
    return { hint: `Fully invoiced${inReviewSuffix}`, fullyInvoiced: true };
  }
  if (totals.approved > EPSILON) {
    return {
      hint: `${formatAud(totals.approved)} of ${formatAud(expected)} invoiced${inReviewSuffix}`,
    };
  }
  if (totals.inReview > EPSILON) {
    return { hint: `${formatAud(totals.inReview)} awaiting review` };
  }
  return {};
}

function toGroups(
  payables: Payables,
  invoicedTotals?: ReadonlyMap<string, PayableInvoicedTotals>,
): CatalogueGroup[] {
  return [
    {
      heading: "Milestones",
      rows: payables.milestones.map((m) => ({
        payableId: m.payableId,
        label: m.label,
        price: m.expected !== null ? formatAud(m.expected) : "— variable",
        ...milestoneDrawState(m.expected, invoicedTotals?.get(m.payableId)),
      })),
    },
    {
      heading: "Products & services",
      rows: payables.products.map((p) => ({
        payableId: p.payableId,
        label: p.label,
        price:
          p.rate !== null
            ? `${formatAud(p.rate)}${p.unit ? ` / ${p.unit}` : ""}`
            : "— variable",
      })),
    },
    {
      heading: "SOWs",
      rows: payables.sows.map((s) => ({
        payableId: s.payableId,
        label: s.label,
        price: "— balance",
      })),
    },
    {
      heading: "Other",
      rows: payables.other.map((o) => ({
        payableId: o.payableId,
        label: o.label,
        price: "— balance",
      })),
    },
  ].filter((g) => g.rows.length > 0);
}

export default function ChargeCatalogue({
  payables,
  selected,
  onToggle,
  disabled,
  invoicedTotals,
}: ChargeCatalogueProps) {
  const [search, setSearch] = useState("");
  const allGroups = toGroups(payables, invoicedTotals);
  if (allGroups.length === 0) {
    return (
      <p className="text-body-sm text-slate-500 dark:text-slate-400">
        Nothing invoiceable is set up on this contract yet — continue to add
        manual charges against PO lines.
      </p>
    );
  }
  // Same convention as the PO form's tag pickers: long registers get a
  // search, small catalogues stay uncluttered. Filtering only affects
  // rendering — selection lives in the modal, so hidden ticked rows stay
  // selected.
  const totalRows = allGroups.reduce((n, g) => n + g.rows.length, 0);
  const searchActive = totalRows > SOW_SEARCH_THRESHOLD;
  const groups = searchActive
    ? allGroups
        .map((g) => ({
          ...g,
          rows: g.rows.filter((r) => matchesSowSearch(r.label, search)),
        }))
        .filter((g) => g.rows.length > 0)
    : allGroups;
  const matchCount = groups.reduce((n, g) => n + g.rows.length, 0);
  return (
    <div className="space-y-3" data-testid="charge-catalogue">
      {searchActive && (
        <>
          <input
            type="search"
            className="input w-full"
            placeholder="Search charges…"
            aria-label="Search charges"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
          />
          {/* Persistent polite live region: focus stays in the search input
              while the list changes, so this is the only channel assistive
              tech hears the result through. Persistent (not conditionally
              rendered) because dynamically inserted live regions are
              unreliably announced. Visible only in the zero-result state —
              it doubles as the no-match message; with matches the count is
              sr-only so sighted users see no extra chrome. */}
          <p
            role="status"
            className={clsx(
              matchCount === 0
                ? "text-body-sm text-slate-500 dark:text-slate-400"
                : "sr-only",
            )}
          >
            {search.trim()
              ? matchCount === 0
                ? `No charges match “${search.trim()}”.`
                : matchCount === 1
                  ? "1 charge shown."
                  : `${matchCount} charges shown.`
              : ""}
          </p>
        </>
      )}
      {groups.map((group) => (
        <fieldset key={group.heading}>
          <legend className="text-overline uppercase text-slate-500 dark:text-slate-400">
            {group.heading}
          </legend>
          <ul className="mt-1 space-y-1">
            {group.rows.map((row) => {
              const id = `catalogue-${row.payableId}`;
              // A selected row must stay tickable even when fully invoiced,
              // or the operator couldn't untick it (same rule as the PO
              // form's tag pickers).
              const rowDisabled =
                disabled ||
                (row.fullyInvoiced === true && !selected.has(row.payableId));
              return (
                <li key={row.payableId}>
                  <label
                    htmlFor={id}
                    className={clsx(
                      "flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 dark:border-slate-700",
                      rowDisabled
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60",
                    )}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      className="h-4 w-4 rounded-sm border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-900"
                      checked={selected.has(row.payableId)}
                      onChange={() => onToggle(row.payableId)}
                      disabled={rowDisabled}
                      aria-describedby={row.hint ? `${id}-hint` : undefined}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body-sm text-slate-900 dark:text-slate-100">
                        {row.label}
                      </span>
                      {row.hint && (
                        <span
                          id={`${id}-hint`}
                          className="block text-caption text-slate-500 dark:text-slate-400"
                        >
                          {row.hint}
                        </span>
                      )}
                    </span>
                    <span className="text-caption tabular-nums text-slate-500 dark:text-slate-400">
                      {row.price}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
    </div>
  );
}
